class CountdownAnnouncementComponent extends HTMLElement {
  constructor() {
    super();
    this.endDate = null;
    this.endTime = null;
    this.intervalId = null;
    this.expiredMessage = null;
  }

  connectedCallback() {
    // Use a small delay to ensure DOM is fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.init(), 100);
      });
    } else {
      // Use setTimeout to ensure element is fully connected
      setTimeout(() => this.init(), 100);
    }
  }

  init() {
    // Prevent double initialization
    if (this.intervalId !== null) {
      return;
    }

    this.countdownTimer = this.querySelector('.countdown_timer');
    if (!this.countdownTimer) {
      console.warn('Countdown timer element not found');
      return;
    }

    const endDateStr = this.countdownTimer.dataset.endDate;
    const endTimeStr = this.countdownTimer.dataset.endTime || '23:59';

    if (!endDateStr || endDateStr.trim() === '') {
      console.warn('Countdown end date is not set');
      return;
    }

    // Parse the end date and time
    const dateParts = endDateStr.split('-');
    if (dateParts.length !== 3) {
      console.error('Invalid date format. Expected YYYY-MM-DD, got:', endDateStr);
      return;
    }

    const [year, month, day] = dateParts.map(Number);
    const [hours, minutes] = endTimeStr.split(':').map(Number);

    // Validate parsed values
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      console.error('Invalid date or time values:', { year, month, day, hours, minutes });
      return;
    }

    this.endDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Validate the date object
    if (Number.isNaN(this.endDate.getTime())) {
      console.error('Invalid date object created:', this.endDate);
      return;
    }

    // Get expired message from section settings (stored in data attribute)
    const sectionElement = this.closest('section');
    if (sectionElement) {
      this.expiredMessage = sectionElement.dataset.expiredMessage || 'This offer has ended';
    } else {
      this.expiredMessage = 'This offer has ended';
    }

    // Start the countdown
    this.updateCountdown();
    this.intervalId = setInterval(() => this.updateCountdown(), 1000);
  }

  updateCountdown() {
    const now = new Date();
    const timeLeft = this.endDate - now;

    if (timeLeft <= 0) {
      this.handleExpired();
      return;
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    this.updateDisplay('days', days);
    this.updateDisplay('hours', hours);
    this.updateDisplay('minutes', minutes);
    this.updateDisplay('seconds', seconds);
  }

  updateDisplay(unit, value) {
    const valueElement = this.countdownTimer?.querySelector(`[data-unit="${unit}"]`);
    if (valueElement) {
      valueElement.textContent = String(value).padStart(2, '0');
    }
  }

  handleExpired() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    const container = this.querySelector('.countdown_container');
    if (container && this.expiredMessage) {
      container.innerHTML = `<div class="countdown_expired">${this.expiredMessage}</div>`;
    }
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

if (!customElements.get('countdown-announcement-component')) {
  customElements.define('countdown-announcement-component', CountdownAnnouncementComponent);
}

// Initialize any existing elements that might already be in the DOM when script loads
// This handles cases where the element exists before the custom element is defined
(function initExistingElements() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExistingElements);
    return;
  }

  // Wait a bit for custom elements to upgrade
  setTimeout(() => {
    const existingElements = document.querySelectorAll('countdown-announcement-component');
    existingElements.forEach((el) => {
      // If element hasn't been initialized (no intervalId), trigger connectedCallback manually
      if (!el.intervalId && el.querySelector('.countdown_timer')) {
        el.connectedCallback();
      }
    });
  }, 200);
})();
